import { ReactNode, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { IconCheck, IconInfoCircle, IconMessagePlus } from '@tabler/icons-react';

import { SubjectOverrides, SubjectWithCandidates } from '@/types/subject-candidate';
import { CandidateRow } from '../CandidateRow';
import classes from './SubjectCandidateCard.module.css';

const MAX_ITERATIONS = 3;

interface SubjectCandidateCardProps {
  subject: SubjectWithCandidates;
  disabled?: boolean;
  onSelectCandidate: (subjectId: string, candidateId: string, overrides?: SubjectOverrides) => void;
  onSubmitClarification: (subjectId: string, additionalContext: string) => void;
}

export function SubjectCandidateCard({
  subject,
  disabled = false,
  onSelectCandidate,
  onSubmitClarification,
}: Readonly<SubjectCandidateCardProps>) {
  const [clarifyOpen, setClarifyOpen] = useState(false);
  const [clarificationText, setClarificationText] = useState('');

  const displayIteration = Math.max(subject.iteration, 1);
  const clarificationsUsed = Math.max(subject.iteration - 1, 0);
  const clarificationsRemaining = Math.max(MAX_ITERATIONS - clarificationsUsed, 0);
  const canClarify = !disabled && !subject.is_resolved && clarificationsRemaining > 0;
  const selectedCandidate = subject.candidates.find((c) => c.is_selected) ?? null;

  const handleClarify = () => {
    const text = clarificationText.trim();
    if (!text) {
      return;
    }
    onSubmitClarification(subject.subject_id, text);
    setClarificationText('');
    setClarifyOpen(false);
  };

  let body: ReactNode;
  if (subject.is_resolved) {
    body = (
      <Alert variant="light" color="green" icon={<IconCheck size={16} />}>
        {selectedCandidate
          ? `Identified as ${selectedCandidate.name}.`
          : 'Subject identified. Research will continue once all subjects on this report are resolved.'}
      </Alert>
    );
  } else if (subject.candidates.length === 0) {
    body = (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Identifying candidates...
        </Text>
      </Group>
    );
  } else {
    body = (
      <>
        <Stack gap="sm">
          {subject.candidates.map((candidate) => (
            <CandidateRow
              key={candidate.id}
              candidate={candidate}
              subjectName={subject.subject_name}
              disabled={disabled}
              onSelect={(candidateId, overrides) =>
                onSelectCandidate(subject.subject_id, candidateId, overrides)
              }
            />
          ))}
        </Stack>

        <Stack gap="xs" className={classes.clarifySection}>
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm" c="dimmed">
              None of these match?
            </Text>
            {clarificationsRemaining <= 1 && (
              <Badge color="yellow" variant="light" size="sm">
                {clarificationsRemaining === 0
                  ? 'No clarifications remaining'
                  : 'Last clarification attempt'}
              </Badge>
            )}
          </Group>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconMessagePlus size={14} />}
            onClick={() => setClarifyOpen((v) => !v)}
            disabled={!canClarify}
          >
            {clarifyOpen ? 'Cancel clarification' : 'Provide more context'}
          </Button>
          <Collapse in={clarifyOpen && canClarify}>
            <Stack gap="xs">
              <Alert variant="light" color="blue" icon={<IconInfoCircle size={14} />} p="xs">
                <Text size="xs">
                  Describe the subject you mean — include employer, location, role, or any
                  distinguishing detail. Submitting will run a new identification round (typically
                  30–120 seconds).
                </Text>
              </Alert>
              <Textarea
                value={clarificationText}
                onChange={(e) => setClarificationText(e.currentTarget.value)}
                placeholder="e.g. The Jack Wang I'm looking for works at a VC firm in Berlin, not the author."
                autosize
                minRows={3}
                maxRows={8}
                disabled={disabled}
              />
              <Group justify="flex-end">
                <Button
                  size="xs"
                  onClick={handleClarify}
                  disabled={disabled || !clarificationText.trim()}
                >
                  Submit clarification
                </Button>
              </Group>
            </Stack>
          </Collapse>
        </Stack>
      </>
    );
  }

  return (
    <Card withBorder className={classes.card}>
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Stack gap={2}>
            <Title order={4}>{subject.subject_name}</Title>
            {subject.iteration > 0 && (
              <Text size="xs" c="dimmed">
                Attempt {displayIteration} of {MAX_ITERATIONS}
              </Text>
            )}
          </Stack>
          {subject.is_resolved && (
            <Badge color="green" variant="light" leftSection={<IconCheck size={12} />} size="lg">
              Confirmed
            </Badge>
          )}
        </Group>

        {body}
      </Stack>
    </Card>
  );
}
